import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const TeamSection = () => {
  const { t, currentLang } = useLanguage();

  // Translations for labels
  const getEyebrowText = () => {
    const labels = {
      en: 'THE TEAM',
      'es-es': 'EL EQUIPO',
    };
    return labels[currentLang] || labels.en;
  };

  const getLinkedInLabel = () => {
    const labels = {
      en: 'LinkedIn Profile',
      'es-es': 'Perfil LinkedIn',
    };
    return labels[currentLang] || labels.en;
  };

  const getBadgeLabel = (type) => {
    const labels = {
      founder: {
        en: 'Founder',
        'es-es': 'Fundador',
      },
      partner: {
        en: 'Partner',
        'es-es': 'Socio',
      },
    };
    return labels[type]?.[currentLang] || labels[type]?.en || type;
  };

  const teamMembers = [
    {
      name: 'Patricio Rojas',
      badge: 'founder',
      role: t('team.member1Role') || 'Founder & CEO, Head of Investments',
      bio: t('team.member1Bio') || 'Serial entrepreneur and investor with over 15 years building and scaling companies across Europe and Latin America. Patricio leads deal sourcing, capital structuring, and strategic partnerships.',
      image: 'https://static.prod-images.emergentagent.com/jobs/8df7ff7a-821e-4c74-bff5-e498b3912af8/images/d1ef651beac0e7ee4006ff885bd2bced29a45e6ab8d20edc68c174d1dbdbb8d5.jpeg',
      linkedin: 'https://www.linkedin.com/in/patriciorojashubner/',
    },
    {
      name: 'Adilson Sequeira',
      badge: 'partner',
      role: t('team.member2Role') || 'Partner, Head of Execution and Operations',
      bio: t('team.member2Bio') || 'Operations and growth specialist with deep experience in scaling commercial teams, optimizing revenue structures, and building institutional-grade processes for high-growth companies.',
      image: 'https://static.prod-images.emergentagent.com/jobs/8df7ff7a-821e-4c74-bff5-e498b3912af8/images/800022569ad41cfa0ea3a455242344262f38946a2fc9504b21458912db47c3f9.jpeg',
      linkedin: 'https://linkedin.com/in/adilsonsequeira',
    },
  ];

  return (
    <section 
      data-testid="team-section"
      className="py-16 md:py-24 bg-[#F7F8F9]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          {/* Eyebrow */}
          <span className="inline-block font-body text-xs font-semibold tracking-[0.2em] uppercase text-[#60B5AF] mb-4">
            {getEyebrowText()}
          </span>
          
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A3A4A] tracking-tight mb-3">
            {t('team.title') || 'Our Team'}
          </h2>
          <p className="font-body text-[#1A3A4A]/60 text-base max-w-xl mx-auto">
            {t('team.subtitle') || 'The people behind the capital, the strategy, and the execution.'}
          </p>
        </motion.div>

        {/* Team Cards - Editorial Design */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {/* Card Layout - Horizontal on larger screens */}
              <div className="flex flex-col sm:flex-row h-full">
                {/* Photo - Left side, portrait crop */}
                <div className="sm:w-2/5 h-64 sm:h-auto relative overflow-hidden flex-shrink-0">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A4A]/10 via-transparent to-transparent" />
                </div>
                
                {/* Content - Right side */}
                <div className="sm:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                  {/* Badge */}
                  <span className="inline-flex items-center self-start px-3 py-1 rounded-full bg-[#1A3A4A]/5 border border-[#1A3A4A]/10 text-[#1A3A4A] font-body text-xs font-semibold tracking-wide uppercase mb-3">
                    {getBadgeLabel(member.badge)}
                  </span>
                  
                  {/* Name */}
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-[#1A3A4A] mb-1">
                    {member.name}
                  </h3>
                  
                  {/* Role */}
                  <p className="font-body text-sm font-medium text-[#60B5AF] mb-4">
                    {member.role}
                  </p>
                  
                  {/* Bio */}
                  <p className="font-body text-sm text-[#1A3A4A]/60 leading-relaxed mb-6 line-clamp-4">
                    {member.bio}
                  </p>
                  
                  {/* LinkedIn Button - Secondary style */}
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 self-start px-5 py-2.5 bg-[#1A3A4A]/5 hover:bg-[#1A3A4A] text-[#1A3A4A] hover:text-white rounded-full transition-all duration-300 font-body text-sm font-medium group/btn"
                    data-testid={`team-linkedin-${index}`}
                  >
                    <Linkedin className="w-4 h-4" />
                    <span>{getLinkedInLabel()}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 -ml-2 group-hover/btn:opacity-100 group-hover/btn:ml-0 transition-all duration-300" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
